/**
 * Affiliate landing icons.
 *
 * Inline SVGs rather than bundled asset files, matching `components/ui/icons.tsx`
 * — the affiliate icons live here because nothing outside the landing page uses
 * them. Single-colour marks paint with `currentColor` so the surrounding CSS
 * controls them from a token; the two-tone marks read their palette from
 * affiliate tokens instead of hard-coded hex.
 */
import type { IconProps } from '../../components/ui/icons';

/** Heart in a circle — "Human Accountability". */
export function AffiliateHeartIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      <path
        d="M9.498 5.79306C10.918 3.88906 13.053 3.33306 15.017 3.86806C17.137 4.44506 19.001 6.26606 19.62 8.80206C19.652 8.93206 19.6797 9.06206 19.703 9.19206C18.8094 8.87365 17.8375 8.84913 16.929 9.12206C15.642 8.17006 14.048 8.01006 12.631 8.53206C10.856 9.18706 9.47 10.8481 9.149 12.9381C8.739 15.6141 10.369 18.0181 12.674 20.0621L13.062 20.3981C12.749 20.4201 12.431 20.3711 12.127 20.3061C11.9708 20.2725 11.8154 20.2351 11.661 20.1941L11.124 20.0441C6.35 18.7011 3.154 16.6001 2.237 13.4601C1.505 10.9541 2.209 8.44506 3.757 6.88506C5.191 5.44006 7.317 4.85406 9.498 5.79306ZM11.126 13.2411C11.554 10.4491 14.783 9.07306 16.441 11.4691C16.4569 11.4913 16.4809 11.5064 16.5079 11.5111C16.5348 11.5158 16.5625 11.5097 16.585 11.4941C18.962 9.81006 21.525 12.2071 20.972 14.9771C20.652 16.5837 19.162 17.9171 16.502 18.9771L16.067 19.1471L15.804 19.2551C15.577 19.3441 15.337 19.4151 15.12 19.3771C14.904 19.3391 14.703 19.1891 14.52 19.0291L14.21 18.7491C11.8967 16.7584 10.8687 14.9224 11.126 13.2411Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Robi's face — "Robi AI Buddy". */
export function AffiliateRobiIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.4918 3.92291L11.3056 6.45342L10.614 6.60229L10.398 6.72631L10.2687 6.95509L10.2124 7.24721L6.41575 7.68925L5.93475 7.82579C4.56195 8.21548 3.49938 9.30491 3.14408 10.687L2.85598 12.6917C2.83756 12.6897 2.83187 12.7171 2.80017 12.6917C2.77207 12.7271 2.74406 12.7624 2.72597 12.7624C1.77472 12.7627 1.00408 14.1031 1.0047 15.7561C1.00533 17.4091 1.6963 18.3256 2.61488 18.648C2.75954 18.6988 2.96447 18.7565 3.20557 18.7324C3.45872 20.2634 5.05104 21.705 6.78092 21.7799L11.7729 21.9963L16.7763 21.6768C18.4944 21.5671 19.9537 20.3794 20.4099 18.7193C21.7941 18.6962 22.5967 17.222 22.5961 15.569C22.5955 13.916 21.8238 12.5763 20.8726 12.5766C20.7746 12.5766 20.6786 12.5909 20.5852 12.6182L20.4849 11.5805C20.2757 9.41701 18.5975 7.68609 16.4415 7.41021L13.3715 7.24701C13.3715 7.24701 13.2974 6.91523 13.2852 6.85536C13.2731 6.79548 13.2456 6.74734 13.1795 6.67948C13.1385 6.6374 13.1137 6.62877 12.9896 6.58561C12.738 6.49807 12.2777 6.45776 12.2777 6.45776L12.2346 6.1352L12.0931 3.90952C11.995 3.95329 11.8883 3.97733 11.7765 3.97733C11.6768 3.97733 11.581 3.95817 11.4918 3.92291Z"
        fill="currentColor"
      />
      <rect
        x="4.78473"
        y="9.30432"
        width="14.0094"
        height="10.9439"
        rx="2.94267"
        transform="rotate(-0.383954 4.78473 9.30432)"
        fill="currentColor"
      />
      <rect
        x="7.656"
        y="12.5613"
        width="2.50339"
        height="3.83469"
        rx="1.2517"
        transform="rotate(-0.197211 7.656 12.5613)"
        fill="var(--color-affiliate-icon-inverse)"
      />
      <rect
        x="-0.025942"
        y="0.0266016"
        width="2.47359"
        height="3.83469"
        rx="1.23679"
        transform="matrix(-0.999921 0.0125526 0.0125526 0.999921 15.8811 12.3691)"
        fill="var(--color-affiliate-icon-inverse)"
      />
      <path
        d="M13.4871 17.2915C13.4516 17.362 13.4076 17.4188 13.3593 17.465C12.9011 17.8066 12.3452 17.6943 11.8111 17.7273C11.2773 17.7072 10.7122 17.8217 10.2272 17.5256C10.1754 17.484 10.1275 17.4311 10.0888 17.3627C10.061 17.436 10.0482 17.518 10.0545 17.6041C10.2251 18.4463 11.147 18.7259 11.8212 18.7256C12.4889 18.7176 13.4456 18.3963 13.5378 17.5291C13.5367 17.4435 13.5182 17.3639 13.4871 17.2915Z"
        fill="var(--color-affiliate-icon-inverse)"
      />
      <path
        d="M10.1611 7.03426C10.1611 6.8416 10.2815 6.66906 10.4652 6.61124C10.6261 6.56062 10.8225 6.5044 10.981 6.47612C11.2922 6.42057 11.7885 6.4137 11.7885 6.4137C11.7885 6.4137 12.2926 6.41882 12.6084 6.47612C12.7637 6.5043 12.9559 6.56006 13.1138 6.61044C13.2966 6.66875 13.4159 6.84078 13.4159 7.03263V7.35397C13.4159 7.60696 13.2108 7.81206 12.9579 7.81206H10.6192C10.3662 7.81206 10.1611 7.60696 10.1611 7.35397V7.03426Z"
        fill="currentColor"
      />
      <path
        d="M7.2806 11.9437C7.2806 11.9437 7.20516 12.0271 7.08346 11.9935C6.98608 11.9667 7.01285 11.8698 7.08826 11.7863C7.23913 11.6194 7.64062 11.2088 8.02394 11.0539C8.46264 10.8883 8.78818 10.8478 9.27744 10.8785C9.3815 10.8812 9.45881 10.9807 9.46978 11.0359C9.48075 11.0911 9.46069 11.1637 9.40532 11.1745C9.07309 11.2392 8.54651 11.1591 8.06542 11.3782C7.72131 11.5349 7.2806 11.9437 7.2806 11.9437Z"
        fill="var(--color-affiliate-icon-inverse)"
      />
      <path
        d="M16.1314 11.9437C16.1314 11.9437 16.2068 12.0271 16.3285 11.9935C16.4259 11.9667 16.3991 11.8698 16.3237 11.7863C16.1729 11.6194 15.7714 11.2088 15.388 11.0539C14.9494 10.8883 14.6238 10.8478 14.1345 10.8785C14.0305 10.8812 13.9532 10.9807 13.9422 11.0359C13.9312 11.0911 13.9513 11.1637 14.0067 11.1745C14.3389 11.2392 14.8655 11.1591 15.3466 11.3782C15.6907 11.5349 16.1314 11.9437 16.1314 11.9437Z"
        fill="var(--color-affiliate-icon-inverse)"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.1294 2.08546C12.0215 2.03077 11.9022 2.00037 11.7765 2.00037C11.2972 2.00037 10.9086 2.44293 10.9086 2.98886C10.9086 3.42121 11.1523 3.78874 11.4918 3.92295C11.581 3.9582 11.6768 3.97736 11.7765 3.97736C11.8882 3.97736 11.995 3.95332 12.0931 3.90955C12.4159 3.76548 12.6445 3.40757 12.6445 2.98886C12.6445 2.7535 12.5722 2.53734 12.4516 2.36759C12.3658 2.24679 12.2555 2.14948 12.1294 2.08546Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Chart trending up in a rounded frame — "Sustainable Progress" / "Grow with us". */
export function AffiliateGrowthIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.464 3.464C2 4.93 2 7.286 2 12C2 16.714 2 19.071 3.464 20.535C4.93 22 7.286 22 12 22C16.714 22 19.071 22 20.535 20.535C22 19.072 22 16.714 22 12C22 7.286 22 4.929 20.535 3.464C19.072 2 16.714 2 12 2C7.286 2 4.929 2 3.464 3.464ZM13.75 10C13.75 10.414 14.086 10.75 14.5 10.75H15.19L13.177 12.763C13.1538 12.7863 13.1262 12.8048 13.0958 12.8174C13.0654 12.83 13.0329 12.8364 13 12.8364C12.9671 12.8364 12.9346 12.83 12.9042 12.8174C12.8738 12.8048 12.8462 12.7863 12.823 12.763L11.237 11.177C10.9088 10.8491 10.4639 10.6649 10 10.6649C9.53609 10.6649 9.09115 10.8491 8.763 11.177L6.47 13.47C6.39631 13.5387 6.33721 13.6215 6.29622 13.7135C6.25523 13.8055 6.23319 13.9048 6.23141 14.0055C6.22963 14.1062 6.24816 14.2062 6.28588 14.2996C6.3236 14.393 6.37974 14.4778 6.45096 14.549C6.52218 14.6203 6.60701 14.6764 6.7004 14.7141C6.79379 14.7518 6.89382 14.7704 6.99452 14.7686C7.09523 14.7668 7.19454 14.7448 7.28654 14.7038C7.37854 14.6628 7.46134 14.6037 7.53 14.53L9.823 12.237C9.84622 12.2137 9.87381 12.1952 9.90418 12.1826C9.93456 12.17 9.96712 12.1636 10 12.1636C10.0329 12.1636 10.0654 12.17 10.0958 12.1826C10.1262 12.1952 10.1538 12.2137 10.177 12.237L11.763 13.823C12.0912 14.1509 12.5361 14.3351 13 14.3351C13.4639 14.3351 13.9088 14.1509 14.237 13.823L16.25 11.811V12.5C16.25 12.6989 16.329 12.8897 16.4697 13.0303C16.6103 13.171 16.8011 13.25 17 13.25C17.1989 13.25 17.3897 13.171 17.5303 13.0303C17.671 12.8897 17.75 12.6989 17.75 12.5V10C17.75 9.80109 17.671 9.61032 17.5303 9.46967C17.3897 9.32902 17.1989 9.25 17 9.25H14.5C14.3011 9.25 14.1103 9.32902 13.9697 9.46967C13.829 9.61032 13.75 9.80109 13.75 10Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Megaphone — "Promote a meaningful product".
 * Replaces the legacy raster-in-SVG notice mark, which could not be recoloured.
 */
export function AffiliateMegaphoneIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden {...rest}>
      <path
        d="M19.4 3.2a1.2 1.2 0 0 0-1.27.12L11.6 8.3H6.8A2.8 2.8 0 0 0 4 11.1v1.8a2.8 2.8 0 0 0 2.8 2.8h.6l1.1 4.5a1.4 1.4 0 0 0 1.36 1.05h1.1a1.2 1.2 0 0 0 1.17-1.48l-.96-4.07h.43l6.53 4.98a1.2 1.2 0 0 0 1.93-.95V4.28a1.2 1.2 0 0 0-.66-1.08ZM9.9 20.05l-1.05-4.35h1.16l1.03 4.35H9.9Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Amber star badge on the "App Rating" floating stat. */
export function AffiliateRatingBadgeIcon({ size = 32, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden {...rest}>
      <path
        d="M0 15.998C0 7.16254 7.16254 0 15.998 0C24.8334 0 31.996 7.16254 31.996 15.998C31.996 24.8334 24.8334 31.996 15.998 31.996C7.16254 31.996 0 24.8334 0 15.998Z"
        fill="var(--color-affiliate-rating-surface)"
      />
      <path
        d="M15.6814 9.53044C15.7106 9.47144 15.7557 9.42177 15.8117 9.38705C15.8676 9.35232 15.9321 9.33392 15.998 9.33392C16.0638 9.33392 16.1283 9.35232 16.1843 9.38705C16.2402 9.42177 16.2853 9.47144 16.3145 9.53044L17.8539 12.6486C17.9553 12.8538 18.105 13.0314 18.2902 13.166C18.4753 13.3007 18.6904 13.3884 18.9169 13.4216L22.3595 13.9254C22.4248 13.9349 22.4861 13.9624 22.5365 14.0049C22.5869 14.0473 22.6244 14.1031 22.6448 14.1657C22.6652 14.2284 22.6676 14.2956 22.6518 14.3596C22.636 14.4236 22.6027 14.4819 22.5555 14.5279L20.0657 16.9523C19.9016 17.1123 19.7787 17.3098 19.7078 17.5278C19.6368 17.7458 19.62 17.9778 19.6586 18.2038L20.2463 21.6292C20.2579 21.6944 20.2508 21.7615 20.226 21.8229C20.2012 21.8843 20.1597 21.9375 20.1061 21.9764C20.0525 22.0153 19.9891 22.0383 19.923 22.0429C19.857 22.0475 19.791 22.0335 19.7325 22.0024L16.655 20.3843C16.4523 20.2778 16.2267 20.2222 15.9976 20.2222C15.7686 20.2222 15.543 20.2778 15.3402 20.3843L12.2634 22.0024C12.205 22.0333 12.139 22.0472 12.0731 22.0425C12.0072 22.0378 11.9439 22.0147 11.8904 21.9758C11.8369 21.937 11.7955 21.8839 11.7707 21.8226C11.7459 21.7613 11.7388 21.6943 11.7503 21.6292L12.3374 18.2045C12.3761 17.9784 12.3593 17.7462 12.2884 17.5281C12.2175 17.3099 12.0945 17.1123 11.9302 16.9523L9.44047 14.5285C9.39288 14.4826 9.35916 14.4242 9.34315 14.36C9.32713 14.2958 9.32947 14.2284 9.34989 14.1655C9.37031 14.1025 9.40799 14.0466 9.45864 14.0041C9.5093 13.9615 9.57089 13.934 9.63639 13.9248L13.0784 13.4216C13.3052 13.3886 13.5205 13.301 13.7059 13.1664C13.8913 13.0317 14.0412 12.854 14.1427 12.6486L15.6814 9.53044Z"
        fill="var(--color-affiliate-rating-fill)"
        stroke="var(--color-affiliate-rating-stroke)"
        strokeWidth="1.33282"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Check badge used as the Robi feature-list bullet. */
export function AffiliateCheckBulletIcon({ size = 20, ...rest }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden {...rest}>
      <path
        d="M0 9.99822C0 4.47636 4.47636 0 9.99822 0C15.5201 0 19.9964 4.47636 19.9964 9.99822C19.9964 15.5201 15.5201 19.9964 9.99822 19.9964C4.47636 19.9964 0 15.5201 0 9.99822Z"
        fill="var(--color-affiliate-check-surface)"
      />
      <path
        d="M13.9981 6.99829L8.49831 12.4981L5.99841 9.99817"
        stroke="var(--color-affiliate-check-mark)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
